// Booking Flow Types - Aligned with Supabase Database Schema
import type {
  FurnitureTransportBuildingType,
  FurnitureTransportElevatorType,
} from '@/lib/pricing/furniture-transport-calculator-config';
import type { WindowTypeId, WindowTypeQuantity } from '@/lib/pricing/window-cleaning-calculator';

export enum ServiceType {
  HOME_CLEANING = 'home_cleaning',
  OFFICE_CLEANING = 'office_cleaning',
  BUSINESS_CLEANING = 'business_cleaning',
  HOARDING_CLEANING = 'hoarding_cleaning',
  MOVE_IN_OUT = 'move_in_out',
  POST_CONSTRUCTION = 'post_construction',
  WINDOW_CLEANING = 'window_cleaning',
  STAIRWELL_CLEANING = 'stairwell_cleaning',
  FURNITURE_TRANSPORT = 'furniture_transport',
}

export enum CleaningFrequency {
  ONCE = 'once',
  WEEKLY = 'weekly',
  BI_WEEKLY = 'bi_weekly',
  MONTHLY = 'monthly',
  CUSTOM = 'custom',
}

export enum EffortLevel {
  REGULAR = 'regular',
  EXPERT = 'expert',
}

export enum PropertySizeTier {
  TIER_1 = 'tier_1', // 25-40m²
  TIER_2 = 'tier_2', // 45-60m²
  TIER_3 = 'tier_3', // 65-80m²
  TIER_4 = 'tier_4', // 85-100m²
  TIER_5 = 'tier_5', // 105-150m²
  TIER_6 = 'tier_6', // 150-200m²
  TIER_7 = 'tier_7', // 201-275m²
  TIER_8 = 'tier_8', // 276-350m²
}

export enum BookingStep {
  SERVICE_SELECTION = 0,
  PROPERTY_DETAILS = 1,
}

// Property Details Types
export interface PropertyDetails {
  rooms: number;
  bathrooms: number;
  squareFootage: number;
  backyardSquareMeters?: number;
  neighborhood?: string;
  region?: string;
  accessInstructions?: string;
  propertySizeTier?: PropertySizeTier;
}

export interface ConstructionServiceAddress {
  street: string;
  houseNumber: string;
  apartmentNumber: string;
  building: string;
  entranceNumber: string;
  floor: string;
  intercomCode: string;
  neighborhood: string;
  city: string;
  country: string;
}

export interface ConstructionBookingDetails {
  customerType: 'individual' | 'legal_entity';
  sqm: number;
  windows: number;
  dateTime: { date: string; start: string; end: string } | null;
  addons: string[];
  address: ConstructionServiceAddress;
}

// Service-specific interfaces
export interface HoardingCleaningDetails extends PropertyDetails {
  serviceType: ServiceType.HOARDING_CLEANING;
  cleaningLevel: 'light' | 'moderate' | 'heavy' | 'extreme';
  lastCleanedDate?: string;
  hasStains: boolean;
  hasOdors: boolean;
  requiresDisinfection: boolean;
  /** Customer requests removal and disposal of the accumulated items. */
  wasteDisposal?: boolean;
}

export interface MoveInOutDetails extends PropertyDetails {
  serviceType: ServiceType.MOVE_IN_OUT;
  moveType: 'move_in' | 'move_out' | 'both';
  propertyCondition: 'excellent' | 'good' | 'fair' | 'poor';
  hasAppliances: boolean;
  // For "both" mode - two properties
  from?: {
    squareFootage: number;
    rooms?: number;
    bathrooms?: number;
  };
  to?: {
    squareFootage: number;
    rooms?: number;
    bathrooms?: number;
  };
  // For "both" mode - two date/time selectors
  dateTime_from?: {
    date: Date | null;
    time: string | null;
  };
  dateTime_to?: {
    date: Date | null;
    time: string | null;
  };
}

export interface PostConstructionDetails extends PropertyDetails {
  serviceType: ServiceType.POST_CONSTRUCTION;
  constructionType: 'renovation' | 'new_build' | 'other';
  dustLevel: 'light' | 'heavy' | 'extreme';
  hasPaintResidue: boolean;
  hasDrywallDust: boolean;
  requiresHepaFilter: boolean;
  construction?: ConstructionBookingDetails;
}

export interface WindowCleaningDetails extends PropertyDetails {
  serviceType: ServiceType.WINDOW_CLEANING;
  customerType?: 'private' | 'company';
  windowCount: number;
  windowHeight: 'ground' | 'medium' | 'high' | 'very_high';
  hasHardToReach: boolean;
  requiresLadder: boolean;
  includesScreens: boolean;
  includesTracks: boolean;
  /** Configurator mode: price by window type or by glass area. */
  windowMode?: 'type' | 'area';
  /** Per-type window/blind breakdown (type mode). */
  windowTypeQuantities?: Partial<Record<WindowTypeId, WindowTypeQuantity>>;
  /** Glass/blind area breakdown (area mode). */
  windowArea?: { windowSqm: number; blindsSqm: number };
  /** Cleaning after renovation adds a surcharge. */
  afterRenovation?: boolean;
  /** Free-text note (access, hard-to-reach glass). */
  windowNotes?: string;
}

export interface FurnitureTransportDetails {
  serviceType: ServiceType.FURNITURE_TRANSPORT;
  propertySizeTier?: PropertySizeTier;
  furnitureVolume: number;
  itemCount: number;
  bulkyItems: number;
  transportDistanceKm: number;
  floorsAtPickup: number;
  floorsAtDropoff: number;
  pickupHasElevator: boolean;
  dropoffHasElevator: boolean;
  assemblyRequired: boolean;
  packingRequired: boolean;
  numberOfMovers: number;
  dropoffAddressLine: string;
  dropoffNeighborhood: string;
  dropoffCity: string;
  accessInstructions?: string;
  /** Item quantities picked in the moving calculator, keyed by catalogue id. */
  transportItems?: Record<string, number>;
  /** Chosen item variants in the moving calculator, keyed by catalogue id. */
  transportVariants?: Record<string, string>;
  /** Building type at the pickup address. */
  pickupBuildingType?: FurnitureTransportBuildingType;
  /** Building type at the dropoff address. */
  dropoffBuildingType?: FurnitureTransportBuildingType;
  /** Elevator type at the pickup address. */
  pickupElevatorType?: FurnitureTransportElevatorType;
  /** Elevator type at the dropoff address. */
  dropoffElevatorType?: FurnitureTransportElevatorType;
  /** Piano or safe needs special handling and equipment. */
  pianoOrSafeRequired?: boolean;
}

export type TaskFrequency = 'every_visit' | 'monthly' | 'quarterly' | 'on_demand';

export interface StairwellTask {
  id: string;
  enabled: boolean;
  frequency: TaskFrequency;
}

export interface StairwellCleaningDetails extends PropertyDetails {
  serviceType: ServiceType.STAIRWELL_CLEANING;
  customerType?: 'private' | 'company';
  cleaningFrequency?: CleaningFrequency;
  numberOfFloors: number;
  numberOfStaircases?: number;
  hasHandrails: boolean;
  hasCarpet: boolean;
  requiresDeepCleaning: boolean;
  requiresInitialDeepClean?: boolean;
  hasElevator?: boolean;
  tasks?: {
    basement?: StairwellTask;
    trashRoom?: StairwellTask;
    mailboxes?: StairwellTask;
    entrance?: StairwellTask;
    gardening?: StairwellTask;
    handrails?: StairwellTask;
    doors?: StairwellTask;
    windows?: StairwellTask;
  };
}

export interface OfficeDetails {
  workstations: number;
  meetingRooms: number;
  commonAreas: number;
  squareFootage: number;
  bathrooms?: number;
  numberOfCleaners?: number; // Number of cleaners for office cleaning
  neighborhood?: string;
  region?: string;
  accessInstructions?: string;
  propertySizeTier?: PropertySizeTier;
  hasKitchen: boolean;
  hasReception: boolean;
  businessType: 'startup' | 'corporate' | 'retail' | 'medical' | 'other';
}

export interface BusinessDetails {
  // Business identification
  businessType:
    | 'office'
    | 'medical'
    | 'retail'
    | 'restaurant'
    | 'gym'
    | 'salon'
    | 'warehouse'
    | 'medical_dental'
    | 'retail_store'
    | 'restaurant_cafe'
    | 'school'
    | 'fitness_gym'
    | 'salon_beauty'
    | 'coworking'
    | 'other';
  otherText?: string; // Free-text field for "Other" business type
  squareFootage: number;
  neighborhood?: string;
  region?: string;
  accessInstructions?: string;

  // Cleaning frequency (integrated into details step)
  cleaningFrequency: 'daily' | 'weekly' | 'monthly';
  cleaningCount: number; // 1-10 cleanings per period

  // Space characteristics
  floorType: 'tiles' | 'concrete' | 'carpet' | 'wood_laminate' | 'other';

  // Traffic patterns (adaptive based on cleaningFrequency)
  visitorCount: number;
  visitorFrequency: 'daily' | 'weekly' | 'monthly';

  // Business priorities
  priority: 'quality' | 'price' | 'reliability';

  // Contract selection (optional - moved to effort step)
  contractType?: 'one-time' | '3-month' | '6-month' | '12-month';

  // Optional legacy fields for compatibility
  workstations?: number;
  meetingRooms?: number;
  commonAreas?: number;
  propertySizeTier?: PropertySizeTier;
}

// Union type for all property detail types
export type AllPropertyDetails =
  | PropertyDetails
  | OfficeDetails
  | BusinessDetails
  | HoardingCleaningDetails
  | MoveInOutDetails
  | PostConstructionDetails
  | WindowCleaningDetails
  | StairwellCleaningDetails
  | FurnitureTransportDetails;

// Estimate Types
export interface Estimate {
  id: string;
  basePrice: number;
  duration: number; // in minutes
  frequency: CleaningFrequency;
  effortLevel?: EffortLevel;
  propertySizeTier?: PropertySizeTier;
  squareMeters?: number;
  isEstimate: boolean;
  addOns: EstimateAddOn[];
  discounts: EstimateDiscount[];
  totalPrice: number;
  currency: string;
  validUntil: string;
  breakdown: EstimateBreakdown;
  accessInstructions?: string;
}

export interface EstimateAddOn {
  id: string;
  name: string;
  price: number;
  quantity?: number;
  isSelected: boolean;
}

export interface EstimateDiscount {
  id: string;
  name: string;
  type: 'percentage' | 'fixed';
  value: number;
  description: string;
}

export interface EstimateBreakdown {
  // Display values (what user sees in breakdown)
  baseService: number; // Original base price before discount (displayed as "Base Service")
  taxes: number; // Taxes calculated on original base + add-ons (displayed as "Tax (20%)")
  discountAmount: number; // Total discount amount (displayed in "Discounts" section)
  total: number; // Final amount to pay (displayed as "To pay")

  // Additional calculation fields
  addOns: number;
  frequencyMultiplier: number;
  effortMultiplier?: number;
  discounts?: number; // Legacy field, use discountAmount instead
}

// Scheduling Types
export interface TimeSlot {
  start: string; // ISO time string (HH:MM)
  end: string; // ISO time string (HH:MM)
  isAvailable: boolean;
}

export interface BookingSchedule {
  id: string;
  date: string; // ISO date string
  timeSlot: TimeSlot;
  duration: number; // in minutes
  providerId?: string;
  isAvailable: boolean;
  notes?: string;
  addOns?: Record<string, number>;
  accessInstructions?: string; // Access instructions from schedule step
}

// Re-export canonical types
export type { CapacityTimeSlot, BookedSlot } from './availability';

export interface DetailsStepResult {
  propertyDetails: PropertyDetails | OfficeDetails | BusinessDetails | FurnitureTransportDetails;
  cleaningFrequency: CleaningFrequency | null;
  addOns: Record<string, number>;
  effortLevel?: EffortLevel;
  schedule?: BookingSchedule;
}

// Main Booking Flow State
export interface BookingFlowState {
  currentStep: BookingStep;
  serviceType: ServiceType | null;
  cleaningFrequency: CleaningFrequency | null;
  propertyDetails:
    PropertyDetails | OfficeDetails | BusinessDetails | FurnitureTransportDetails | null;
  addOns: Record<string, number>;
  estimate: Estimate | null;
  selectedEffort: EffortLevel | null;
  sizeTier: PropertySizeTier | null;
  schedule: BookingSchedule | null;
  isSubmitting: boolean;
  errors: Record<string, string>;
  metadata: BookingMetadata;
  detailsVersion?: number;
  scheduleVersion?: number;
}

export interface BookingMetadata {
  sessionId: string;
  startedAt: string;
  lastUpdatedAt: string;
  source: 'direct' | 'marketing' | 'referral' | 'organic';
  utmParams?: Record<string, string>;
  deviceInfo?: {
    type: 'mobile' | 'tablet' | 'desktop';
    browser: string;
    os: string;
  };
}

// Form Data Types
export interface OneTimeCleaningData extends PropertyDetails {
  serviceType: ServiceType;
  frequency: CleaningFrequency.ONCE;
  specialInstructions?: string;
  preferredDate?: string;
}

export interface RegularCleaningData extends PropertyDetails {
  serviceType: ServiceType;
  frequency: Exclude<CleaningFrequency, CleaningFrequency.ONCE>;
  effortLevel: EffortLevel;
  startDate: string;
  endDate?: string;
  specialInstructions?: string;
}

// API Response Types
export interface BookingFlowResponse {
  success: boolean;
  data?: unknown;
  error?: string;
  step?: BookingStep;
  redirectUrl?: string;
}

// Component Props Types
export interface BookingStepProps {
  onNext: (data: unknown) => void;
  onBack: () => void;
  onSkip?: () => void;
  data?: unknown;
  errors?: Record<string, string>;
  isLoading?: boolean;
}

export interface ServiceSelectionProps extends BookingStepProps {
  preselectedService?: ServiceType;
}

export interface FrequencySelectionProps extends BookingStepProps {
  serviceType: ServiceType;
}

export interface EstimateProps extends BookingStepProps {
  propertyData: PropertyDetails | OfficeDetails | FurnitureTransportDetails;
  serviceType: ServiceType;
  frequency: CleaningFrequency;
}

export interface PackageSelectionProps extends BookingStepProps {
  selectedEffort?: EffortLevel;
  frequency: CleaningFrequency;
}

export interface SchedulingProps extends BookingStepProps {
  estimate: Estimate;
  serviceType: ServiceType;
  frequency: CleaningFrequency;
}

// Validation Schemas (for use with Zod)
export const PropertyDetailsSchema = {
  propertyType: 'string' as const,
  rooms: 'number' as const,
  bathrooms: 'number' as const,
  squareFootage: 'number' as const,
  floors: 'number' as const,
  hasBasement: 'boolean' as const,
  hasAttic: 'boolean' as const,
  hasGarage: 'boolean' as const,
  pets: 'boolean' as const,
  specialRequirements: 'array' as const,
  accessInstructions: 'string' as const,
} as const;

// Utility Types
export type BookingFlowStep = keyof typeof BookingStep;
export type ServiceTypeKey = keyof typeof ServiceType;
export type CleaningFrequencyKey = keyof typeof CleaningFrequency;
export type EffortLevelKey = keyof typeof EffortLevel;
export type PropertySizeTierKey = keyof typeof PropertySizeTier;

// Step condition type
export type StepCondition = (state: BookingFlowState) => boolean;
